import { apiDownload, apiGet, apiPost, apiUpload } from '../../lib/api'
import { withAuth } from '../auth/token'

/** Mirrors the compliance document slices on the API. */
export type DocumentOwnerType = 'Broker' | 'Carrier' | 'Driver' | 'Truck' | 'Trailer'

/**
 * Where a paper stands against the calendar, judged by the server. `NotYetEffective`
 * is a renewal filed ahead of its term (TRKC-84): on file, not yet in force.
 */
export type DocumentStanding = 'NoExpiry' | 'Current' | 'ExpiringSoon' | 'Expired' | 'NotYetEffective'

export interface ComplianceDocument {
  id: string
  type: string
  isCritical: boolean
  issuedOn: string | null
  expiresOn: string | null
  standing: DocumentStanding
  notes: string | null
  /** The policy behind an insurance coverage line; null on every other paper. */
  policyNumber: string | null
  /** The coverage's headline limit in US dollars, when its line shows one. */
  coverageLimit: number | null
  /**
   * The least this coverage may carry and still clear the carrier — the rulebook's
   * number, so "below the minimum" on screen is never a figure of the portal's own.
   */
  minimumLimit: number | null
  /**
   * Shared by every coverage line read from one certificate (TRKC-81): the panel
   * groups them by it, and striking one strikes them all.
   */
  certificateId: string | null
  /**
   * Null, with contentType, on a paper filed on its facts alone — a medical card
   * without its scan (TRKC-83): nothing to download, and the row says so.
   */
  fileName: string | null
  contentType: string | null
  sizeBytes: number
  status: 'Active' | 'Superseded'
  supersededByDocumentId: string | null
  uploadedAt: string
  /**
   * For a line whose term has not begun (TRKC-84): the superseded paper that covers
   * today in its place, resolved by the server down the chain of renewals; null when
   * none does. The panel reads it and never walks the chain itself.
   */
  coveredByDocumentId: string | null
  /**
   * On an upload's own answer: whether the record's compliance standing was re-judged
   * after the paper was filed. False when the record moved underneath the evaluation
   * — the paper is on file either way, so this is a staleness warning, never a failed
   * upload (the activation answer's `standingConfirmed`, on a paper). Null on a list
   * read, which judges nothing.
   */
  ownerStandingConfirmed: boolean | null
}

/**
 * A mirror of the domain's rulebook, used only to offer sensible options, mark the
 * expiry box required and let the file box be left empty where the rulebook allows it
 * — the server enforces the real matrix (pinned by a domain test), so drift here
 * surfaces as a translated 400, never as wrong data.
 */
export const DOCUMENT_TYPES_BY_OWNER: Record<
  DocumentOwnerType,
  ReadonlyArray<{ type: string; expiryRequired: boolean; fileOptional?: boolean }>
> = {
  Broker: [
    { type: 'OperatingAuthority', expiryRequired: false },
    { type: 'CarrierBrokerAgreement', expiryRequired: false },
    { type: 'Bond', expiryRequired: false },
    { type: 'W9', expiryRequired: false },
    { type: 'Other', expiryRequired: false },
  ],
  Carrier: [
    { type: 'InsuranceCertificate', expiryRequired: true },
    { type: 'W9', expiryRequired: false },
    { type: 'OperatingAuthority', expiryRequired: false },
    { type: 'HazmatCert', expiryRequired: true },
    { type: 'Permit', expiryRequired: false },
    { type: 'Other', expiryRequired: false },
  ],
  Driver: [
    { type: 'Cdl', expiryRequired: true },
    // Identified by the driver's name and CDL number, watched by its expiration —
    // the scan is welcome, never required (Hugo, 2026-09-14, TRKC-83).
    { type: 'MedicalCard', expiryRequired: true, fileOptional: true },
    { type: 'HazmatCert', expiryRequired: true },
    { type: 'Twic', expiryRequired: true },
    { type: 'Other', expiryRequired: false },
  ],
  Truck: [
    { type: 'VehicleRegistration', expiryRequired: true },
    { type: 'AnnualInspection', expiryRequired: true },
    { type: 'Carb', expiryRequired: false },
    { type: 'Permit', expiryRequired: false },
    { type: 'Other', expiryRequired: false },
  ],
  Trailer: [
    { type: 'VehicleRegistration', expiryRequired: true },
    { type: 'AnnualInspection', expiryRequired: true },
    { type: 'Other', expiryRequired: false },
  ],
}

/**
 * The papers whose ABSENCE holds a record back, per owner kind — a mirror of the
 * rulebook's required list (the server enforces it; drift here shows as a badge the
 * panel cannot explain). Said under every panel since TRKC-84, not only under a
 * carrier's coverages: "Pending" without "why" sent people to compare dates by hand.
 */
export const REQUIRED_BY_OWNER: Record<DocumentOwnerType, readonly string[]> = {
  Broker: ['OperatingAuthority', 'Bond'],
  Carrier: ['InsuranceGeneralLiability', 'InsuranceAutoLiability', 'InsuranceCargo', 'OperatingAuthority'],
  // The medical card and the annual inspection are no longer required (TRKC-106).
  Driver: ['Cdl'],
  Truck: ['VehicleRegistration'],
  Trailer: ['VehicleRegistration'],
}

/**
 * The select's "Insurance certificate" entry. Not a paper of its own any more:
 * choosing it opens the coverage grid, and what gets filed is one paper per coverage
 * line (TRKC-81). The name survives because rows filed under the old one-date type
 * still read under it.
 */
export const INSURANCE_CERTIFICATE = 'InsuranceCertificate'

/**
 * The five coverage lines an ACORD 25 can carry, in the certificate form's order — a
 * mirror of the rulebook's list, like DOCUMENT_TYPES_BY_OWNER (the server enforces the
 * set; drift here surfaces as a translated 400). `key` is the coverage's name inside
 * error codes (`insurance_cargo_policy_number_required`), which is how a complaint
 * finds its cell. `required` marks the three a carrier must hold to be dispatched, and
 * `minimum` is what each must carry (Hugo's minimums, TRKC-81) — shown as the grid's
 * hint before anything is filed; a filed row carries the server's own number.
 */
export const INSURANCE_COVERAGES = [
  { type: 'InsuranceGeneralLiability', key: 'general_liability', required: true, minimum: 1_000_000 },
  { type: 'InsuranceAutoLiability', key: 'auto_liability', required: true, minimum: 1_000_000 },
  { type: 'InsuranceCargo', key: 'cargo', required: true, minimum: 100_000 },
  { type: 'InsuranceWorkersCompensation', key: 'workers_compensation', required: false, minimum: null },
  { type: 'InsurancePhysicalDamage', key: 'physical_damage', required: false, minimum: null },
] as const

export type InsuranceCoverageType = (typeof INSURANCE_COVERAGES)[number]['type']

export type InsuranceCoverageKey = (typeof INSURANCE_COVERAGES)[number]['key']

export function listDocuments(
  ownerType: DocumentOwnerType,
  ownerId: string,
): Promise<ComplianceDocument[]> {
  return apiGet<ComplianceDocument[]>(
    `/api/documents?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`,
    { headers: withAuth() },
  )
}

export interface UploadDocumentInput {
  ownerType: DocumentOwnerType
  ownerId: string
  type: string
  issuedOn: string | null
  expiresOn: string | null
  notes: string | null
  /** Null files the paper on its facts alone — the server allows it per type. */
  file: File | null
}

export function uploadDocument(input: UploadDocumentInput): Promise<ComplianceDocument> {
  const form = new FormData()
  form.append('ownerType', input.ownerType)
  form.append('ownerId', input.ownerId)
  form.append('type', input.type)

  if (input.issuedOn !== null) {
    form.append('issuedOn', input.issuedOn)
  }

  if (input.expiresOn !== null) {
    form.append('expiresOn', input.expiresOn)
  }

  if (input.notes !== null) {
    form.append('notes', input.notes)
  }

  // No file part at all without a file: an empty part would read as an empty file.
  if (input.file !== null) {
    form.append('file', input.file, input.file.name)
  }

  return apiUpload<ComplianceDocument>('/api/documents', form, { headers: withAuth() })
}

/** One line of the certificate, as typed off the paper. */
export interface CoverageLineInput {
  coverage: InsuranceCoverageType
  policyNumber: string
  effectiveOn: string
  expiresOn: string
  limit: number | null
}

/**
 * Files an ACORD 25 as the coverage lines it lists — one request, one file, one
 * paper per line. The lines ride as JSON inside the multipart form, because the
 * file has to travel in the same request and a five-line grid is not what form
 * fields are for.
 */
export function uploadInsuranceCertificate(input: {
  ownerId: string
  coverages: CoverageLineInput[]
  file: File
}): Promise<ComplianceDocument[]> {
  const form = new FormData()
  form.append('ownerId', input.ownerId)
  form.append('coverages', JSON.stringify(input.coverages))
  form.append('file', input.file, input.file.name)

  return apiUpload<ComplianceDocument[]>('/api/documents/insurance-certificate', form, {
    headers: withAuth(),
  })
}

/**
 * What a strike did. The row is struck and the bytes are gone either way;
 * `standingConfirmed` says whether the record was re-judged afterwards — a strike can
 * move the verdict either way, and false means the badge around the panel may be stale.
 */
export interface DiscardOutcome {
  documentId: string
  standingConfirmed: boolean
}

/**
 * Strikes a mistaken upload: its bytes are destroyed on the server and only an
 * audit tombstone remains — the remedy for the wrong (or confidential) file. A
 * coverage line goes with its whole certificate: the lines share the file.
 */
export function discardDocument(id: string): Promise<DiscardOutcome> {
  return apiPost<DiscardOutcome>(`/api/documents/${id}/discard`, {}, { headers: withAuth() })
}

/**
 * Fetches the guarded bytes and hands them to the browser as a download — a plain
 * <a href> cannot carry the bearer token.
 */
export async function downloadDocument(document_: { id: string; fileName: string }): Promise<void> {
  const blob = await apiDownload(`/api/documents/${document_.id}/download`, {
    headers: withAuth(),
  })

  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    // The row's own name — the one source both the screen and the save dialog share.
    anchor.download = document_.fileName
    anchor.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}
